import { createFileRoute } from '@tanstack/react-router';
import { Settings2Icon } from 'lucide-react';
import { useState } from 'react';

import { FillScreen, HStack, VStack } from '@/components/Layout';
import type { TabWithIndicatorProps } from '@/components/Navigation/HeaderTabs';
import { ProductHeader } from '@/components/ProductHeader';
import { Separator } from '@/components/ui/separator';
import {
  PackageSidebar,
  type SidebarState,
} from '@/features/operation-package/PackageSidebar';
import useDocumentTitle from '@/hooks/useDocumentTitle';
import { useTranslation } from '@/lib/i18n';

import { MockContent } from './qa-operations-core/-components/MockContent';

export const Route = createFileRoute('/_stacktrace/qa-operations-core')({
  component: QaOperationsCorePage,
});

const SIDEBAR_GROUPS = [
  {
    packages: [
      { id: 'test-plan', label: 'テスト計画' },
      { id: 'test-review', label: 'テストレビュー' },
    ],
  },
  {
    packages: [
      { id: 'test-asset-inventory', label: 'テストアセット棚卸し' },
      { id: 'test-asset-registration', label: 'テストアセット登録' },
    ],
  },
  {
    packages: [
      { id: 'defect-inventory', label: '不具合棚卸し' },
      { id: 'enhance-report-inventory', label: 'エンハンス報告棚卸し' },
    ],
  },
];

function QaOperationsCoreLogo({ size = 40 }: { size?: number }) {
  return <Settings2Icon size={size} className="text-primary-on-fill" />;
}

function QaOperationsCorePage() {
  const { t } = useTranslation();
  const title = t('component.sidebar.qaForceOperationsCore');
  useDocumentTitle(title);

  const [activeId, setActiveId] = useState<string>(
    SIDEBAR_GROUPS[0].packages[0].id,
  );
  const [sidebarState, setSidebarState] = useState<SidebarState>('open');

  const activeLabel =
    SIDEBAR_GROUPS.flatMap((group) => group.packages).find(
      (item) => item.id === activeId,
    )?.label ?? '';

  const tabs: TabWithIndicatorProps[] = [
    {
      to: '/qa-operations-core' as const,
      children: 'オペレーションパッケージ',
      activeOptions: { exact: true, includeSearch: true },
    },
    {
      to: '/qa-operations-core/archives' as never,
      children: 'アーカイブ',
      disabled: true,
      activeOptions: { exact: true },
    },
    {
      to: '/qa-operations-core/configs' as never,
      children: '設定',
      disabled: true,
      activeOptions: { exact: true },
    },
  ];

  return (
    <VStack className="h-full">
      <ProductHeader
        Logo={QaOperationsCoreLogo}
        name={title}
        tabs={tabs}
        type="core"
      />
      <FillScreen>
        <HStack className="h-full w-full max-w-screen">
          <PackageSidebar
            groups={[...SIDEBAR_GROUPS]}
            activeId={activeId}
            onSelect={setActiveId}
            sidebarState={sidebarState}
            onStateChange={setSidebarState}
          />
          <Separator orientation="vertical" />
          <MockContent activeLabel={activeLabel} />
        </HStack>
      </FillScreen>
    </VStack>
  );
}
